import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2, MessageCircle, RefreshCw, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { whatsappMerkez } from "../../lib/api";

const DURUM_RENK = {
    GONDERILDI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    HATA: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    ONAY_BEKLIYOR: "text-orange-300 bg-orange-500/10 border-orange-500/30",
    BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
};

const YATAR_RENK = {
    ONAYLANDI: "text-emerald-200 bg-emerald-500/15 border-emerald-400/40",
    REDDEDILDI: "text-rose-200 bg-rose-500/15 border-rose-400/40",
    YANIT_BEKLIYOR: "text-amber-200 bg-amber-500/15 border-amber-400/40",
    BEKLIYOR: "text-sky-200 bg-sky-500/15 border-sky-400/40",
    YOK: "text-slate-300 bg-white/5 border-white/15",
};


function YatarTimeline({ dosyaId }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        if (!dosyaId) return;
        whatsappMerkez.yatarDosyaTimeline(dosyaId).then((r) => setItems(r?.timeline || [])).catch(() => setItems([]));
    }, [dosyaId]);
    if (!items.length) return null;
    return (
        <div className="mt-3 rounded-lg border border-violet-500/20 bg-violet-950/20 p-3 max-h-48 overflow-y-auto">
            <div className="text-[10px] font-semibold text-violet-300 uppercase tracking-wide mb-2">Yatar onay zaman cizelgesi</div>
            <ul className="space-y-1.5">
                {items.map((ev, i) => (
                    <li key={i} className="text-[11px] text-[#aab]">
                        <span className="text-[#F3E5AB]">{String(ev.tarih || "").slice(0, 16).replace("T", " ")}</span>
                        {" · "}{ev.baslik}{ev.detay ? ` — ${ev.detay}` : ""}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function WhatsAppGecmisiSection({ dosyaId }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [ozet, setOzet] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!dosyaId) return;
        setLoading(true);
        try {
            const data = await whatsappMerkez.dosyaGecmisi(dosyaId);
            setRows(data.gonderimler || []);
            setOzet(data.ozet || null);
        } catch {
            setRows([]);
            setOzet(null);
        } finally {
            setLoading(false);
        }
    }, [dosyaId]);

    useEffect(() => {
        load();
    }, [load]);

    async function handleYeniden() {
        if (!dosyaId || busy) return;
        setBusy(true);
        try {
            const res = await whatsappMerkez.dosyaYatarYeniden(dosyaId);
            if (res?.ok) {
                toast.success("Yatar onay mesajı yeniden kuyruğa alındı");
                await load();
            } else {
                toast.error(res?.detail || "Gönderilemedi");
            }
        } catch (e) {
            toast.error(e?.message || "Gönderilemedi");
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return (
            <div className="h-32 flex items-center justify-center rounded-xl border border-white/10 bg-[#121A2F]">
                <Loader2 className="h-6 w-6 animate-spin text-[#25d366]" />
            </div>
        );
    }

    const yatarDurum = ozet?.yatar_wa_durum || "YOK";
    const yatarLabel = ozet?.yatar_wa_durum_label || "WhatsApp yatar yok";

    return (
        <section className="rounded-2xl border border-white/10 bg-[#121A2F]/90 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-[#25d366]" />
                    <h2 className="text-sm font-semibold text-[#f5ecd6]">WhatsApp özeti</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        className="h-8 px-2 rounded border border-white/10 text-[#8b93ae] hover:text-white"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                    <Link to="/yeni/whatsapp-merkezi" className="text-xs text-[#25d366] hover:underline">
                        Merkez →
                    </Link>
                </div>
            </div>

            <div className="px-5 py-4 border-b border-white/5 bg-black/20">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-[11px] border font-medium ${
                            YATAR_RENK[yatarDurum] || YATAR_RENK.YOK
                        }`}
                    >
                        Yatar WA: {yatarLabel}
                    </span>
                    <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-[11px] border ${
                            ozet?.yatar_acilacak_mi
                                ? "text-amber-200 bg-amber-500/15 border-amber-400/40"
                                : "text-slate-300 bg-white/5 border-white/15"
                        }`}
                    >
                        Dosya yatar alanı: {ozet?.yatar_acilacak_mi ? "Evet" : "Hayır"}
                    </span>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={handleYeniden}
                        className="ml-auto h-8 px-3 rounded-lg border border-[#25d366]/40 bg-[#25d366]/15 text-[#25d366] text-xs inline-flex items-center gap-1.5 hover:bg-[#25d366]/25 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Yatar onayını yeniden gönder
                    </button>
                </div>
                {ozet?.son_gelen ? (
                    <p className="text-xs text-[#c5cad8]">
                        <span className="text-[#8b93ae]">Son gelen: </span>
                        {(ozet.son_gelen.mesaj || "").slice(0, 160)}
                        {ozet.son_gelen.tarih ? (
                            <span className="text-[#8b93ae]">
                                {" "}
                                · {new Date(ozet.son_gelen.tarih).toLocaleString("tr-TR")}
                            </span>
                        ) : null}
                    </p>
                ) : (
                    <p className="text-xs text-[#8b93ae]">Bu dosya/müvekkilden henüz gelen WhatsApp yok.</p>
                )}
            </div>

            {!rows.length ? (
                <p className="py-8 text-center text-sm text-[#8b93ae]">Bu dosyaya ait WhatsApp kaydı yok.</p>
            ) : (
                <ul className="divide-y divide-white/5">
                    {rows.map((g) => (
                        <li key={g.id} className="px-5 py-3 hover:bg-white/[0.02]">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-[#f5ecd6]">
                                            {g.sablon_kod || "özel"}
                                        </span>
                                        <span
                                            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] border ${
                                                DURUM_RENK[g.durum] || DURUM_RENK.BEKLIYOR
                                            }`}
                                        >
                                            {g.durum_display}
                                        </span>
                                        {g.kanal_display && (
                                            <span className="text-[10px] text-[#8b93ae]">{g.kanal_display}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-[#8b93ae] mt-1">
                                        {g.olusturma_tarihi
                                            ? new Date(g.olusturma_tarihi).toLocaleString("tr-TR")
                                            : "—"}
                                    </p>
                                </div>
                                {g.wa_me_url && g.durum !== "GONDERILDI" && (
                                    <a
                                        href={g.wa_me_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-[#25d366]"
                                    >
                                        wa.me <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                            <pre className="mt-2 text-[11px] text-[#c5cad8] whitespace-pre-wrap bg-black/20 rounded-lg p-2 max-h-24 overflow-y-auto">
                                {g.mesaj}
                            </pre>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
